import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { startQuiz, submitQuiz } from '../lib/api';
import { SUBJECTS } from '../lib/utils';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { ArrowLeft, ArrowRight, Send, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const QuizPage = () => {
  const { subject } = useParams();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const subjectInfo = SUBJECTS[subject];

  useEffect(() => {
    loadQuiz();
  }, [subject]);

  const loadQuiz = async () => {
    try {
      const response = await startQuiz(subject);
      if (response.data.questions.length === 0) {
        toast.error('Não há questões disponíveis para esta matéria');
        navigate('/dashboard');
        return;
      }
      setQuestions(response.data.questions);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao carregar simulado');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOption = (option) => {
    setAnswers({
      ...answers,
      [questions[currentIndex].id]: option,
    });
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleSubmit = async () => {
    const unanswered = questions.filter(q => !answers[q.id]);
    if (unanswered.length > 0) {
      toast.error(`Você tem ${unanswered.length} questão(ões) sem resposta`);
      return;
    }

    setSubmitting(true);

    try {
      const answerList = questions.map(q => ({
        question_id: q.id,
        selected_option: answers[q.id],
      }));

      const response = await submitQuiz(subject, answerList);
      navigate(`/result/${response.data.id}`, { state: { result: response.data } });
    } catch (err) {
      toast.error('Erro ao enviar simulado');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-zinc-400 mx-auto mb-4" />
            <p className="text-zinc-500">Carregando questões...</p>
          </div>
        </div>
      </Layout>
    );
  }

  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;
  const answeredCount = Object.keys(answers).length;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto animate-fadeIn">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-zinc-500 hover:text-zinc-900 mb-4 transition-colors"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar para matérias
          </button>
          
          <h1 className="text-2xl md:text-3xl font-bold text-zinc-900 mb-2">
            {subjectInfo?.name}
          </h1>
          
          <div className="flex items-center gap-4 text-sm text-zinc-500">
            <span>Questão {currentIndex + 1} de {questions.length}</span>
            <span>•</span>
            <span>{answeredCount} respondidas</span>
          </div>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <Progress value={progress} className="h-2" />
        </div>

        {/* Question Card */}
        <div className="bg-white border border-zinc-200 rounded-xl p-6 md:p-8 mb-6">
          <div className="mb-6">
            <span className="text-sm text-zinc-500 uppercase tracking-wider">
              Questão {currentIndex + 1}
            </span>
            <p className="text-lg md:text-xl text-zinc-900 mt-2 leading-relaxed">
              {currentQuestion.question_text}
            </p>
          </div>

          {/* Options */}
          <div className="space-y-3">
            {['a', 'b', 'c', 'd'].map((option) => {
              const optionText = currentQuestion[`option_${option}`];
              const isSelected = answers[currentQuestion.id] === option;
              
              return (
                <button
                  key={option}
                  onClick={() => handleSelectOption(option)}
                  className={`option-btn ${isSelected ? 'selected' : ''}`}
                  data-testid={`option-${option}`}
                >
                  <div className="flex items-start gap-4">
                    <span className={`
                      w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                      ${isSelected ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-600'}
                    `}>
                      {option.toUpperCase()}
                    </span>
                    <span className="flex-1 text-left pt-1">{optionText}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Question Navigator */}
        <div className="bg-white border border-zinc-200 rounded-xl p-4 mb-6">
          <p className="text-sm text-zinc-500 mb-3">Navegação rápida:</p>
          <div className="flex flex-wrap gap-2">
            {questions.map((q, index) => {
              const isAnswered = !!answers[q.id];
              const isCurrent = index === currentIndex;
              
              return (
                <button
                  key={q.id}
                  onClick={() => setCurrentIndex(index)}
                  className={`
                    w-10 h-10 rounded-lg text-sm font-medium transition-all
                    ${isCurrent ? 'bg-zinc-900 text-white' : ''}
                    ${!isCurrent && isAnswered ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' : ''}
                    ${!isCurrent && !isAnswered ? 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200' : ''}
                  `}
                  data-testid={`nav-question-${index}`}
                >
                  {index + 1}
                </button>
              );
            })}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between gap-4">
          <Button
            onClick={handlePrev}
            disabled={currentIndex === 0}
            variant="outline"
            className="flex items-center gap-2"
            data-testid="prev-question"
          >
            <ArrowLeft className="w-4 h-4" />
            Anterior
          </Button>

          {currentIndex === questions.length - 1 ? (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="submit-quiz"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              Finalizar Simulado
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              className="flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="next-question"
            >
              Próxima
              <ArrowRight className="w-4 h-4" />
            </Button>
          )}
        </div>

        {/* Warning */}
        {answeredCount < questions.length && (
          <div className="mt-6 flex items-center gap-3 p-4 bg-orange-50 border border-orange-200 rounded-lg text-orange-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm">
              Você ainda tem {questions.length - answeredCount} questão(ões) sem resposta.
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default QuizPage;
